import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BellIcon, 
  EnvelopeIcon, 
  DevicePhoneMobileIcon, 
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const NotificationSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    smsEnabled: false,
    subscriptionReminders: true,
    paymentReminders: true,
    maintenanceAlerts: true
  });

  useEffect(() => {
    if (settings?.notifications) {
      setNotificationSettings(settings.notifications);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newNotificationSettings = {
      ...notificationSettings,
      [field]: value
    };
    setNotificationSettings(newNotificationSettings);
    onSettingsChange({ ...settings, notifications: newNotificationSettings });
    onHasChanges(true);
  };

  const notificationTypes = [
    {
      key: 'subscriptionReminders',
      name: 'Rappels d\'abonnement',
      description: 'Notifications avant expiration des abonnements',
      icon: CalendarDaysIcon,
      color: 'text-blue-400'
    },
    {
      key: 'paymentReminders',
      name: 'Rappels de paiement',
      description: 'Notifications pour les paiements en retard',
      icon: EnvelopeIcon,
      color: 'text-green-400'
    },
    {
      key: 'maintenanceAlerts',
      name: 'Alertes de maintenance',
      description: 'Notifications pour la maintenance des équipements',
      icon: ExclamationTriangleIcon,
      color: 'text-orange-400'
    }
  ];

  const getActiveNotificationsCount = () => {
    return notificationTypes.filter(type => notificationSettings[type.key]).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <BellIcon className="h-6 w-6 text-yellow-400" />
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Paramètres de notification</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm">Configuration des notifications et alertes système</p>
        </div>
      </div>

      {/* Méthodes de notification */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Notifications Email */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <EnvelopeIcon className="h-5 w-5 text-blue-400" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-50">Notifications Email</h4>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-700 dark:text-slate-300 text-sm">Activer les notifications par email</p>
                <p className="text-slate-600 dark:text-slate-400 text-xs">Envoyer des emails pour les alertes importantes</p>
              </div>
              <button
                onClick={() => handleChange('emailEnabled', !notificationSettings.emailEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationSettings.emailEnabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationSettings.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className={`bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3 ${
              notificationSettings.emailEnabled ? 'border border-green-500/20' : 'border border-red-500/20'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {notificationSettings.emailEnabled ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-400" />
                ) : (
                  <XCircleIcon className="h-4 w-4 text-red-400" />
                )}
                <p className="text-slate-700 dark:text-slate-300 text-sm font-medium">
                  {notificationSettings.emailEnabled ? 'Activé' : 'Désactivé'}
                </p>
              </div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">
                {notificationSettings.emailEnabled 
                  ? 'Les notifications seront envoyées par email'
                  : 'Aucune notification par email ne sera envoyée'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* Notifications SMS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <DevicePhoneMobileIcon className="h-5 w-5 text-green-400" />
            <h4 className="font-semibold text-slate-900 dark:text-slate-50">Notifications SMS</h4>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-700 dark:text-slate-300 text-sm">Activer les notifications par SMS</p>
                <p className="text-slate-600 dark:text-slate-400 text-xs">Envoyer des SMS pour les alertes urgentes</p>
              </div>
              <button
                onClick={() => handleChange('smsEnabled', !notificationSettings.smsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationSettings.smsEnabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationSettings.smsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className={`bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3 ${
              notificationSettings.smsEnabled ? 'border border-green-500/20' : 'border border-red-500/20'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {notificationSettings.smsEnabled ? (
                  <CheckCircleIcon className="h-4 w-4 text-green-400" />
                ) : (
                  <XCircleIcon className="h-4 w-4 text-red-400" />
                )}
                <p className="text-slate-700 dark:text-slate-300 text-sm font-medium">
                  {notificationSettings.smsEnabled ? 'Activé' : 'Désactivé'}
                </p>
              </div>
              <p className="text-slate-600 dark:text-slate-400 text-xs">
                {notificationSettings.smsEnabled 
                  ? 'Les notifications urgentes seront envoyées par SMS'
                  : 'Aucune notification par SMS ne sera envoyée'
                }
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Types de notifications */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <BellIcon className="h-5 w-5 text-yellow-400" />
          <h4 className="font-semibold text-slate-900 dark:text-slate-50">Types de notifications</h4>
        </div>
        
        <div className="space-y-4">
          {notificationTypes.map((type, index) => {
            const Icon = type.icon;
            return (
              <motion.div
                key={type.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-slate-100/80 dark:bg-slate-800/40 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${type.color}`} />
                  <div>
                    <p className="text-slate-700 dark:text-slate-300 text-sm font-medium">{type.name}</p>
                    <p className="text-slate-600 dark:text-slate-400 text-xs">{type.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleChange(type.key, !notificationSettings[type.key])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notificationSettings[type.key] ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notificationSettings[type.key] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Résumé des notifications */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">Résumé des notifications</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <EnvelopeIcon className="h-6 w-6 text-blue-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
              {notificationSettings.emailEnabled ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-slate-600 dark:text-slate-400 text-xs">Email</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <DevicePhoneMobileIcon className="h-6 w-6 text-green-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
              {notificationSettings.smsEnabled ? 'Activé' : 'Désactivé'}
            </div>
            <div className="text-slate-600 dark:text-slate-400 text-xs">SMS</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <BellIcon className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{getActiveNotificationsCount()}</div>
            <div className="text-slate-600 dark:text-slate-400 text-xs">Types actifs</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <CheckCircleIcon className="h-6 w-6 text-purple-400 mx-auto mb-2" />
            <div className="text-sm font-bold text-slate-900 dark:text-slate-50">
              {(notificationSettings.emailEnabled || notificationSettings.smsEnabled) ? 'Configuré' : 'Inactif'}
            </div>
            <div className="text-slate-600 dark:text-slate-400 text-xs">État global</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NotificationSettings; 